import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["autoSaveStatus", "questionnaireForm"]
  static outlets = ["tax-organizer--progress", "tax-organizer--business-status", "tax-organizer"]

  static values = {
    updateUrl: String
  }

  connect() {
    this.autoSaveTimeout = null
    this.uploadingQuestionId = null
    this.isSaving = false
    this.pendingSave = null // Stores the next save to run after current completes
  }

  disconnect() {
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }
  }

  // Auto-save functionality
  autoSave(event) {
    // Skip auto-save for elements marked with data-skip-auto-save
    if (event.target.dataset.skipAutoSave === 'true') {
      return
    }

    // If a save is in progress, queue this one for later
    if (this.isSaving) {
      // Store the pending save details to run after current save completes
      const questionId = this.getQuestionIdFromEvent(event)
      const fieldKey = this.getFieldKeyFromEvent(event)
      this.pendingSave = { questionId, fieldKey }
      return
    }

    // Clear existing timeout
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }

    // Find the question ID and field key from the input field that triggered the save
    const questionId = this.getQuestionIdFromEvent(event)
    const fieldKey = this.getFieldKeyFromEvent(event)

    // Validate the input field using native HTML5 validation
    // For masked inputs, use the exposed validation element
    const targetElement = event.target
    const validationElement = targetElement.maskedInputValidationElement || targetElement

    if (validationElement && !validationElement.checkValidity()) {
      // Field is invalid, show the browser's native validation message
      validationElement.reportValidity()
      validationElement.classList.add('is-invalid')

      // Show validation error icon on field status
      if (questionId && this.hasTaxOrganizerBusinessStatusOutlet) {
        this.taxOrganizerBusinessStatusOutlet.showValidationError(questionId, fieldKey)
      }

      // Don't proceed with auto-save
      this.updateAutoSaveStatus('Validation failed', 'text-danger')
      setTimeout(() => {
        this.updateAutoSaveStatus('Auto-save enabled', 'text-muted')
      }, 2000)
      return
    }

    // Field is valid - clear any validation error state
    validationElement.classList.remove('is-invalid')
    if (questionId && this.hasTaxOrganizerBusinessStatusOutlet) {
      this.taxOrganizerBusinessStatusOutlet.clearValidationError(questionId, fieldKey)
    }

    // Handle document-linked questions (those with conditional file uploads)
    // Use data attribute to find upload section - more reliable than DOM traversal
    if (targetElement.type === 'radio' && targetElement.dataset.uploadSectionId) {
      const uploadSection = document.getElementById(targetElement.dataset.uploadSectionId)

      if (uploadSection) {
        // Show upload section if "yes" is selected, hide if "no" is selected
        uploadSection.style.display = targetElement.value === 'yes' ? 'block' : 'none'

        // Skip auto-save for "yes" answers - file upload will set the answer
        // But DO save "no" answers to clear the question and any uploaded documents
        if (targetElement.value === 'yes') {
          return
        }
      }
    }

    // Update status to show saving
    this.updateAutoSaveStatus('Saving...', 'text-warning')

    // Debounce the save operation based on input type
    const isFileInput = event.target.type === 'file'
    const isDiscrete = event.target.type === 'radio' || event.target.type === 'checkbox'
    let delay = 1000 // Default for text inputs (debounce typing)
    if (isFileInput) delay = 100 // Quick for file uploads
    if (isDiscrete) delay = 0 // Immediate for radio/checkbox clicks

    // Store question ID for file uploads so we can disable inputs during upload
    if (isFileInput && questionId) {
      this.uploadingQuestionId = questionId
    }

    this.autoSaveTimeout = setTimeout(() => {
      this.performAutoSave(questionId, fieldKey)
    }, delay)
  }

  // Perform the actual auto-save
  async performAutoSave(questionId, fieldKey = null) {
    try {
      // Set saving flag to prevent concurrent requests
      this.isSaving = true

      // Show spinner on question badge if we have a question ID (individual organizer)
      if (questionId && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.showQuestionSaving(questionId)
      }

      // Show spinner on field status if we have business status outlet
      if (questionId && this.hasTaxOrganizerBusinessStatusOutlet) {
        this.taxOrganizerBusinessStatusOutlet.showSaving(questionId, fieldKey)
      }

      const formData = new FormData(this.questionnaireFormTarget)

      // Check if form data contains any file uploads
      const hasFileUploads = Array.from(formData.entries()).some(([key, value]) =>
        value instanceof File && value.size > 0
      )

      // Disable question inputs during file upload
      if (hasFileUploads && this.uploadingQuestionId) {
        this.disableQuestionInputs(this.uploadingQuestionId)
      }

      // Always use turbo_stream format for consistent UI updates
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        body: formData,
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })

      if (response.status === 403) {
        alert('You do not have permission to make changes to this tax organizer.')
        this.updateAutoSaveStatus('Permission denied', 'text-danger')

        // Hide spinner on question badge
        if (questionId && this.hasTaxOrganizerProgressOutlet) {
          this.taxOrganizerProgressOutlet.hideQuestionSaving(questionId)
        }

        // Clear saving flag - don't process pending saves on permission error
        this.isSaving = false
        this.pendingSave = null // Clear any pending save since user can't save
        return
      }

      // Let Turbo handle the stream response (works for both 2xx and 422 responses)
      const text = await response.text()
      Turbo.renderStreamMessage(text)

      // Hide spinner on question badge
      if (questionId && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(questionId)
      }

      // Re-enable question inputs after file upload
      if (this.uploadingQuestionId) {
        this.enableQuestionInputs(this.uploadingQuestionId)
        this.uploadingQuestionId = null
      }

      if (response.ok) {
        // Clear file inputs after successful upload to prevent re-uploading on subsequent saves
        if (hasFileUploads) {
          this.clearFileInputs()
        }

        // Update status for success
        const statusText = hasFileUploads ? 'File uploaded' : 'Saved'
        this.updateAutoSaveStatus(statusText, 'text-success')
        this.updateLastSavedTime()

        // Show success on field status (business organizer)
        if (questionId && this.hasTaxOrganizerBusinessStatusOutlet) {
          this.taxOrganizerBusinessStatusOutlet.showSaved(questionId, fieldKey)
        }

        // Reset status after 2 seconds
        setTimeout(() => {
          this.updateAutoSaveStatus('Auto-save enabled', 'text-muted')
        }, 2000)
      } else {
        // Update status for validation error (422)
        this.updateAutoSaveStatus('Validation failed', 'text-danger')

        // Show error on field status (business organizer)
        if (questionId && this.hasTaxOrganizerBusinessStatusOutlet) {
          this.taxOrganizerBusinessStatusOutlet.showError(questionId, fieldKey)
        }

        // Reset status after 3 seconds
        setTimeout(() => {
          this.updateAutoSaveStatus('Auto-save enabled', 'text-muted')
        }, 3000)
      }

      // Clear saving flag and check for pending saves
      this.isSaving = false
      this.processPendingSave()
    } catch (error) {
      console.error('Auto-save failed:', error)
      this.updateAutoSaveStatus('Save failed', 'text-danger')

      // Hide spinner on question badge on error (individual organizer)
      if (questionId && this.hasTaxOrganizerProgressOutlet) {
        this.taxOrganizerProgressOutlet.hideQuestionSaving(questionId)
      }

      // Show error on field status (business organizer)
      if (questionId && this.hasTaxOrganizerBusinessStatusOutlet) {
        this.taxOrganizerBusinessStatusOutlet.showError(questionId, fieldKey)
      }

      // Re-enable question inputs if file upload failed
      if (this.uploadingQuestionId) {
        this.enableQuestionInputs(this.uploadingQuestionId)
        this.uploadingQuestionId = null
      }

      // Clear saving flag and check for pending saves
      this.isSaving = false
      this.processPendingSave()

      // Reset status after 3 seconds
      setTimeout(() => {
        this.updateAutoSaveStatus('Auto-save enabled', 'text-muted')
      }, 3000)
    }
  }

  // Update auto-save status display
  updateAutoSaveStatus(text, className) {
    if (this.hasAutoSaveStatusTarget) {
      this.autoSaveStatusTarget.textContent = text
      this.autoSaveStatusTarget.className = `text-muted ${className}`
    }
  }

  // Update last saved time tooltip
  updateLastSavedTime() {
    if (this.hasAutoSaveStatusTarget) {
      const now = new Date()
      const timeString = now.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      })

      // Update the data attributes
      this.autoSaveStatusTarget.setAttribute('data-last-saved-at', now.getTime())
      this.autoSaveStatusTarget.setAttribute('data-title', `Last saved: ${timeString}`)

      // Recreate the tooltip with the new title
      const tooltipInstance = window.bootstrap?.Tooltip?.getInstance(this.autoSaveStatusTarget)
      if (tooltipInstance) {
        tooltipInstance.dispose()
        new window.bootstrap.Tooltip(this.autoSaveStatusTarget, {
          placement: this.autoSaveStatusTarget.getAttribute('data-placement') || 'bottom',
          title: `Last saved: ${timeString}`,
          trigger: 'hover'
        })
      }
    }
  }

  // Get question ID from the event target
  getQuestionIdFromEvent(event) {
    return event.target.dataset.questionId || null
  }

  // Get field key from the event target (for composite fields like address)
  getFieldKeyFromEvent(event) {
    return event.target.dataset.fieldKey || null
  }

  // Display uploaded documents in the UI (business organizer only)
  displayUploadedDocuments(uploadedDocuments) {
    uploadedDocuments.forEach(doc => {
      const uploadDiv = document.getElementById(`${doc.document_type}_upload`)
      if (!uploadDiv) return

      // Find or create the existing files section
      let existingFilesDiv = uploadDiv.querySelector('.existing-files')
      if (!existingFilesDiv) {
        // Create it after the small help text
        const helpText = uploadDiv.querySelector('small.text-muted')
        if (!helpText) return

        existingFilesDiv = document.createElement('div')
        existingFilesDiv.className = 'existing-files mt-2'
        existingFilesDiv.innerHTML = `
          <small class="text-muted d-block mb-1">
            <strong>Files uploaded:</strong>
          </small>
        `
        helpText.parentNode.insertBefore(existingFilesDiv, helpText.nextSibling)
      }

      // Add the new file to the list
      const fileItem = document.createElement('div')
      fileItem.className = 'd-flex align-items-center mb-1'
      fileItem.innerHTML = `
        <i class="fas fa-check-circle text-success me-2"></i>
        <small class="text-muted">
          ${doc.file_name} (${this.formatFileSize(doc.file_size)})
        </small>
      `
      existingFilesDiv.appendChild(fileItem)

      // Clear the file input
      const fileInput = uploadDiv.querySelector('input[type="file"]')
      if (fileInput) {
        fileInput.value = ''
      }
    })
  }

  // Format file size in human-readable format
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  // Disable all inputs for a specific question during file upload
  disableQuestionInputs(questionId) {
    const inputs = this.element.querySelectorAll(`[data-question-id="${questionId}"]`)
    inputs.forEach(input => {
      // Don't disable the file input itself, only radio buttons and checkboxes
      if (input.type !== 'file') {
        input.disabled = true
        input.style.opacity = '0.6'
      }
    })
  }

  // Re-enable all inputs for a specific question after file upload
  enableQuestionInputs(questionId) {
    const inputs = this.element.querySelectorAll(`[data-question-id="${questionId}"]`)
    inputs.forEach(input => {
      if (input.type !== 'file') {
        input.disabled = false
        input.style.opacity = ''
      }
    })
  }

  // Process any pending save that was queued while a save was in progress
  processPendingSave() {
    if (this.pendingSave) {
      const { questionId, fieldKey } = this.pendingSave
      this.pendingSave = null

      // Trigger the pending save immediately (no debounce delay)
      this.performAutoSave(questionId, fieldKey)
    }
  }

  // Clear all file inputs in the form to prevent re-uploading on subsequent saves
  clearFileInputs() {
    const fileInputs = this.questionnaireFormTarget.querySelectorAll('input[type="file"]')
    fileInputs.forEach(input => {
      input.value = ''
    })
  }
}
